import { Component, OnInit, NgZone, OnDestroy } from "@angular/core";
import {
    ITCIApplication,
    ITCITenantComponentOutput,
    ITCIMetricsStatus,
    TCITenantSelectorType,
    TCIApplicationMonitoringMode,
    TCIMetricsLabelType,
    ITCIApplicationAuditRecord
} from "@tci/common";
import { getAppDetailsRoute, setTimeoutInZone } from "../../../ng-shared/ng-util/tools";
import { AbstractTCITenantComponent } from "../../../ng-tenant/tci-tenant-component";
import { BaseAppDetailMenuComponentInput } from "../../../ng-app-details/ng-app-details-menu";
import { BaseAppListPeekService } from "./base-app-list-peek.service";

import { Subscription } from "rxjs";
import * as moment from "moment";
import * as lodash from "lodash";

@Component({
    selector: TCITenantSelectorType.TCI_APP_LIST_MONITORING_APP_PEEK.toString(),
    templateUrl: "./base-app-list-peek.component.html",
    styleUrls: ["./base-app-list-peek.component.css"]
})
export class BaseAppListPeekComponent<E, I extends BaseAppDetailMenuComponentInput, O extends ITCITenantComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O> implements OnInit, OnDestroy {
    app: ITCIApplication;

    // Assets for the dyn-components
    mode = TCIApplicationMonitoringMode.MODE_APP_LIST;
    appPeekExecinput: BaseAppDetailMenuComponentInput;

    // Data displayed in this container component
    uptime: string;
    endpointsCount;
    lastUpdated = { available: true, usr: "", time: "", action: "" };

    // Input for cpu+memory stats component
    statsData: { cpu: ITCIMetricsStatus[], memory: ITCIMetricsStatus[] } = { cpu: null, memory: null };

    appDetailsRoute: { route: string[], queryParams: Object };
    appEndpointsRoute: { route: string[], queryParams: Object };
    animateRefeshIcon = false;

    private appRefreshSub: Subscription;

    constructor(private ngZone: NgZone, private peekSrv: BaseAppListPeekService) {
        super();

        this.appRefreshSub = peekSrv.getAppRefreshSubject()
            .filter(refr => refr.app.id === this.app.id)
            .subscribe(refr => this.formatAuditData(refr.lastAudit));
    }

    ngOnInit() {
        this.app = this.input.getApplication();

        if (this.peekSrv.showExecutionsData(this.app)) {
            this.appPeekExecinput = new BaseAppDetailMenuComponentInput(
                this.input.tenantAppType,
                TCITenantSelectorType.TCI_APP_LIST_MONITORING_APP_PEEK_EXEC,
                this.input.application,
                this.input.userService,
                this.input.featureToggleService,
                this.input.mode
            );
        }

        this.refresh();
        this.setAppDetailsRoutes();
        this.setAppEndpointsRoutes();
    }

    ngOnDestroy() {
        this.peekSrv.stopRefreshSubscription();
        this.appRefreshSub.unsubscribe();
    }

    refresh(animate = false) {
        this.peekSrv.refreshMetrics(this.app.id);
        this.getMetrics();
        this.getLastUpdateInfo();
        this.getNonPingEndpoints();

        if (animate && this.ngZone && this.ngZone.run) {
            this.animateRefeshIcon = true;
            const cb = () => {
                this.animateRefeshIcon = false;
            };
            setTimeoutInZone(this.ngZone, cb, 2000);
        }
    }

    private getMetrics() {
        if (this.peekSrv && this.peekSrv.getAppMetrics) {
            this.peekSrv.getAppMetrics(this.input.getApplication())
                .filter(res => !lodash.isEmpty(res.labeledAppMetrics))
                .subscribe(res => {
                    if (res.labeledAppMetrics[TCIMetricsLabelType.tci_app_uptime]) {
                        const uptime = lodash.max(
                            res.labeledAppMetrics[TCIMetricsLabelType.tci_app_uptime]
                                .map(ut => lodash.isEmpty(ut) ? 0 : ut.value)
                        );

                        if (uptime > 0) {
                            this.uptime = moment().subtract(uptime, "m").fromNow();
                        }
                    }

                    this.statsData.cpu = res.labeledAppMetrics[TCIMetricsLabelType.tci_app_instances_cpu];
                    this.statsData.memory = res.labeledAppMetrics[TCIMetricsLabelType.tci_app_instances_memory];
                });
        }
    }

    private getLastUpdateInfo() {
        this.peekSrv.getLastUpdateInfo(this.app)
            .subscribe(
                res => this.formatAuditData(res.lastAudit),
                e => {
                    console.error("Audit for peek data not available for", this.app.id, e);
                    this.lastUpdated.available = false;
                }
            );
    }

    private getNonPingEndpoints() {
        const thisObj = this;
        if (this.peekSrv && this.peekSrv.getNonPingEndpoints) {
            this.peekSrv.getNonPingEndpoints(this.app.id)
                .subscribe(
                    endpoints => {
                        thisObj.endpointsCount = endpoints.length;
                    },
                    e => {
                        console.error("Error to get non-ping endpoints", thisObj.app.id, e);
                    }
                );
        }
    }

    private setAppDetailsRoutes() {
        this.appDetailsRoute = getAppDetailsRoute(this.app);
    }

    private setAppEndpointsRoutes() {
        this.peekSrv.getAppEndpointsRoute(this.input)
            .subscribe(res => this.appEndpointsRoute = res);
    }

    private formatAuditData(lastAudit: ITCIApplicationAuditRecord) {
        if (lastAudit) {
            this.lastUpdated.usr = lastAudit.userName;
            this.lastUpdated.time = moment(lastAudit.createdTime).fromNow();
            this.lastUpdated.action = lastAudit.actionSummary;
        } else {
            this.lastUpdated.available = false;
        }
    }
}
